import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import {ACTION_CATEGORY, ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import {ReviewerStatus, ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ReviewSummaryProps as ReviewSummaryContentProps} from 'nav/sidepanel/HomeSidepanel';
import {getDefaultSearchSearchParams} from 'nav/sidepanel/listing/SearchSearchParams';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import * as SidepanelReduxActions from 'nav/sidepanel/SidepanelReduxActions';
import ReviewConstants, {IN_PROGRESS_FILTER, SENT_FOR_REVIEW} from 'notebook/reviews/ReviewConstants';
import {getStore} from 'redux-stores/Store';
import {FilterKey, keyToEmptyFilter} from 'search/SearchConstants';
import * as SearchUtil from 'search/SearchUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import isModelTypeEnabledForListing from 'util/isModelTypeEnabledForListing';

export interface ReviewSummaryItemProps {
  count: number;
  icon: BenchlingIconName;
  iconClassName?: string;
  onClick: () => void;
  text: string;
  reviewStatus: string;
}

const ReviewSummaryItem = ({
  icon,
  iconClassName,
  count,
  text,
  reviewStatus,
  onClick,
}: ReviewSummaryItemProps): JSX.Element => (
  <div
    className={`homeSidepanel-today-item-with-icon ${css(styles.reviewSummaryItem)}`}
    onClick={onClick}
    role="button"
    tabIndex={0}
    {...getTestProps({component: 'ReviewSummaryItem', element: 'button'})}
  >
    <BenchlingIcon className={`homeSidepanel-today-review-icon ${iconClassName || ''}`} icon={icon} />
    <span
      className="whiteLink"
      {...getTestProps({component: 'ReviewSummaryItem', element: 'root', key: reviewStatus})}
      data-amp-safe={false}
    >
      {`${count} ${text}`}
    </span>
  </div>
);

export interface ReviewSummaryProps {
  reviewableType: string;
  reviewSummary: ReviewSummaryContentProps;
}

class ReviewSummary extends React.Component<ReviewSummaryProps> {
  _setReviewFilter = (
    reviewStatusFilter: string,
    roleFilter: string,
    reviewerStatusFilter: ReviewerStatus | null = null
  ): void => {
    trackEvent(
      {
        module: ANALYTICS_MODULE.NAVIGATION,
        actionCategory: ACTION_CATEGORY.READ,
        action: 'review_summary_filter_clicked',
      },
      {
        reviewableType: this.props.reviewableType,
        filter: reviewStatusFilter,
      }
    );

    const modelTypes =
      this.props.reviewableType === 'entry'
        ? [ModelTypeConstants.ENTRY, ModelTypeConstants.STAGE_ENTRY]
        : [ModelTypeConstants.WORKSHEET];
    getStore().dispatch(
      SidepanelReduxActions.openListing(
        NavConstants.Section.SEARCH,
        SearchUtil.mergeFiltersToSearchParams(getDefaultSearchSearchParams(), [
          {
            ...keyToEmptyFilter(FilterKey.TYPES),
            value: modelTypes.filter((type) => isModelTypeEnabledForListing(type)),
          },
          {
            ...keyToEmptyFilter(FilterKey.REVIEW),
            value: {
              review_status: reviewStatusFilter,
              reviewerStatus: reviewerStatusFilter,
              role: roleFilter,
            },
          },
        ])
      )
    );
    SidepanelActions.expand();
  };

  _handleFilterNeedsRevisionEntries = (): void => {
    // When the flag is enabled, use AUTHOR role filter to match the count displayed
    // which only includes entries or worksheets where the user is an author
    const roleFilter = BenchlingConfig.ENABLE_CONSISTENT_REVIEW_SUMMARY_COUNT
      ? ReviewConstants.ROLE_FILTERS.AUTHOR
      : ReviewConstants.ROLE_FILTERS.AUTHOR_OR_AUDITOR;

    this._setReviewFilter(ReviewConstants.REJECTED, roleFilter);
  };

  _handleFilterReadyForReviewEntries = (): void => {
    this._setReviewFilter(
      ReviewConstants.NEEDS_REVIEW,
      ReviewConstants.ROLE_FILTERS.AUDITOR,
      ReviewerStatus.PENDING
    );
  };

  _handleFilterSentForReviewEntries = (): void => {
    this._setReviewFilter(ReviewConstants.NEEDS_REVIEW, ReviewConstants.ROLE_FILTERS.AUTHOR);
  };

  _handleFilterAcceptedEntries = (): void => {
    this._setReviewFilter(ReviewConstants.ACCEPTED, ReviewConstants.ROLE_FILTERS.AUTHOR_OR_AUDITOR);
  };

  _handleFilterInProgressEntries = (): void => {
    // When the flag is enabled, use AUTHOR role filter to match the count displayed
    // which only includes entries where the user is an author
    const roleFilter = BenchlingConfig.ENABLE_CONSISTENT_REVIEW_SUMMARY_COUNT
      ? ReviewConstants.ROLE_FILTERS.AUTHOR
      : ReviewConstants.ROLE_FILTERS.AUTHOR_OR_AUDITOR;

    this._setReviewFilter(ReviewConstants.IN_PROGRESS_FILTER, roleFilter);
  };

  override render(): JSX.Element {
    const {reviewSummary} = this.props;

    // TODO (BNCH-130431): Genericize how showInProgress is assigned for non-HasAuthors reviewables
    const showInProgress = this.props.reviewableType !== 'worksheet';
    const inProgressCount = reviewSummary.inProgress + reviewSummary.retracted;
    const revisionCount = reviewSummary.rejected;
    const readyForReviewCount = reviewSummary.needsReview;
    const sentForReviewCount = reviewSummary.sentForReview;
    const acceptedCount = reviewSummary.accepted;

    const reviewSummaryItems = [
      // Only include in-progress item for entries
      showInProgress && (
        <ReviewSummaryItem
          key="in-progress"
          count={inProgressCount}
          icon={IN_PROGRESS_FILTER}
          iconClassName="homeSidepanel-today-review-icon--inProgress"
          onClick={this._handleFilterInProgressEntries}
          text="in progress"
          reviewStatus={ReviewConstants.IN_PROGRESS_FILTER}
        />
      ),
      <ReviewSummaryItem
        key="needs-revision"
        count={revisionCount}
        icon={ReviewStatus.REJECTED}
        iconClassName="homeSidepanel-today-review-icon--rejected"
        onClick={this._handleFilterNeedsRevisionEntries}
        text={revisionCount === 1 ? 'needs revision' : 'need revision'}
        reviewStatus={ReviewConstants.REJECTED}
      />,
      <ReviewSummaryItem
        key="ready-for-review"
        count={readyForReviewCount}
        icon={ReviewStatus.NEEDS_REVIEW}
        iconClassName="homeSidepanel-today-review-icon--review"
        onClick={this._handleFilterReadyForReviewEntries}
        text="ready for review"
        reviewStatus={ReviewConstants.NEEDS_REVIEW}
      />,
      <ReviewSummaryItem
        key="sent-for-review"
        count={sentForReviewCount}
        icon={SENT_FOR_REVIEW}
        onClick={this._handleFilterSentForReviewEntries}
        text="sent for review"
        reviewStatus={ReviewConstants.SENT_FOR_REVIEW}
      />,
      <ReviewSummaryItem
        key="accepted"
        count={acceptedCount}
        icon={ReviewStatus.ACCEPTED}
        iconClassName="homeSidepanel-today-review-icon--accepted"
        onClick={this._handleFilterAcceptedEntries}
        text={BenchlingConfig.ENABLE_ENTRY_REVIEW_PROCESSES ? 'completed' : 'accepted'}
        reviewStatus={ReviewConstants.ACCEPTED}
      />,
    ];

    return (
      <div className={`homeSidepanel-today-review ${css(styles.reviewSummaryContainer)}`}>
        {reviewSummaryItems}
      </div>
    );
  }
}

export default ReviewSummary;

const styles = StyleSheet.create({
  reviewSummaryContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px 8px',
  },
  reviewSummaryItem: {
    flex: '0 0 calc(50% - 4px)',
    display: 'flex',
    alignItems: 'center',
    minWidth: '0',
  },
});
